import { useCallback, useEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon, SearchIcon } from '@/components/ui/Icons';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { useProjectsColumnSearch } from '@/contexts/ProjectsColumnSearchContext';
import '@/components/Layout/NavbarSearchControl.css';
import './EditableCell.css';
import './ColumnSearchPopover.css';

type ColumnSearchPopoverProps = {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  popoverRef: React.RefObject<HTMLDivElement | null>;
};

export function ColumnSearchPopover({
  title,
  isOpen,
  onClose,
  triggerRef,
  popoverRef,
}: ColumnSearchPopoverProps) {
  const { searchInput, setColumnSearchInput, clearColumnSearchText } = useProjectsColumnSearch();

  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(
    isOpen,
    useCallback(() => {
      onClose();
    }, [onClose]),
    true
  );

  usePopoverPositioning({
    triggerRef: triggerRef as React.RefObject<HTMLElement | null>,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex: 1000,
    dependencies: [title],
  });

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    enabled: shouldRender,
    capture: false,
  });

  useEffect(() => {
    if (!shouldRender) return;
    const t = window.setTimeout(() => {
      const root = popoverRef.current?.querySelector('input');
      root?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [shouldRender, popoverRef]);

  const handleClear = useCallback(() => {
    clearColumnSearchText();
    requestClose();
  }, [clearColumnSearchText, requestClose]);

  const hasAnchor = !!triggerRef.current;
  const popoverAnchorStyle: CSSProperties = {
    position: 'fixed',
    pointerEvents: 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };
  const popoverPanelStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  if (!shouldRender) return null;

  return createPortal(
    <div ref={popoverRef as React.RefObject<HTMLDivElement>} style={popoverAnchorStyle}>
      <OverlayIntrinsicSize>
        <div
          style={popoverPanelStyle}
          className={`editable-cell-popover column-search-popover ${contentClasses}`}
        >
          <div className="editable-cell-popover__header column-search-popover__header">Поиск: {title}</div>
          <div className="editable-cell-popover__form column-search-popover__form">
            <div className="navbar-search column-search-popover__search" role="search">
              <span className="navbar-search__icon" aria-hidden>
                <SearchIcon size={18} />
              </span>
              <input
                type="text"
                className="navbar-search__input"
                enterKeyHint="search"
                value={searchInput}
                onChange={(e) => setColumnSearchInput(e.target.value)}
                placeholder="Поиск проектов…"
                aria-label={`Поиск в столбце ${title}`}
                autoComplete="off"
                spellCheck={false}
              />
              {searchInput.trim().length > 0 ? (
                <button
                  type="button"
                  className="navbar-search__clear"
                  onClick={handleClear}
                  title="Сбросить поиск по столбцу"
                  aria-label="Сбросить поиск по столбцу"
                >
                  <CloseIcon size={18} />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </OverlayIntrinsicSize>
    </div>,
    document.body
  );
}
